import { useCommandHandler, useWhenContext } from "@fleet/shortcuts";
import { COMMAND_IDS } from "@/lib/shortcuts/defaults";
import { useUndo } from "@/lib/undo";
import type { useSearchPalette } from "@/features/search/hooks/use-search-palette";
import type { useTheme } from "@/providers/theme-provider";
import type { NavigateFn } from "@tanstack/solid-router";

interface AppShortcutsOptions {
  searchPalette: ReturnType<typeof useSearchPalette>;
  navigate: NavigateFn;
  theme: ReturnType<typeof useTheme>;
  setShortcutsOpen: (open: boolean) => void;
  setIsSidebarOpen: (f: (prev: boolean) => boolean) => void;
  navigatePage: (isNext: boolean) => void;
}

export function useAppShortcuts(options: AppShortcutsOptions) {
  // ── Shortcuts: push reactive context flags so the engine can evaluate
  // when-clauses. `dialog.open` is owned by the Dialog primitive / shortcuts
  // modal (the single comprehensive source via the interaction context), so it
  // is intentionally not duplicated here.
  useWhenContext({
    "palette.open": () => options.searchPalette.isOpen(),
  });

  const { undoLast } = useUndo();

  // ── Shortcut handlers (declarative; bindings live in lib/shortcuts/defaults.ts)
  useCommandHandler(COMMAND_IDS.PALETTE_OPEN, () => {
    options.searchPalette.toggle();
  });
  // ACTIVITY-CORE L1: Ctrl/Cmd+Z undoes the last action of the session.
  useCommandHandler(COMMAND_IDS.APP_UNDO, () => {
    void undoLast();
  });
  useCommandHandler(COMMAND_IDS.HELP_OPEN, () => {
    options.setShortcutsOpen(true);
  });

  // ── UI toggles ─────────────────────────────────────────────────────────
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_SIDEBAR, () => {
    options.setIsSidebarOpen((prev) => !prev);
  });
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_BLUR, () => {
    void options.theme.toggleBlur();
  });
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_FOLLOW_DEVICE_THEME, () => {
    void options.theme.toggleFollowDevice();
  });
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_BACKGROUND_STYLE, () => {
    options.theme.toggleBackgroundStyle();
  });

  // ── Navigation ─────────────────────────────────────────────────────────
  useCommandHandler(COMMAND_IDS.GOTO_DASHBOARD, () => {
    void options.navigate({ to: "/" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_TRANSACTIONS, () => {
    void options.navigate({ to: "/transactions" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_ACCOUNTS, () => {
    void options.navigate({ to: "/accounts" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_CATEGORIES, () => {
    void options.navigate({ to: "/categories" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_REPORTS, () => {
    void options.navigate({ to: "/reports" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_BUDGETS, () => {
    void options.navigate({ to: "/budgets" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_PROFILE, () => {
    void options.navigate({ to: "/profile" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_THEMES, () => {
    void options.navigate({ to: "/themes" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_SHORTCUTS, () => {
    void options.navigate({ to: "/profile/shortcuts" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_LANGUAGE, () => {
    void options.navigate({ to: "/language" });
  });
  useCommandHandler(COMMAND_IDS.PAGE_NEXT, () => {
    options.navigatePage(true);
  });
  useCommandHandler(COMMAND_IDS.PAGE_PREV, () => {
    options.navigatePage(false);
  });
}
